import {
  CombinedRouteLegType,
  NodeConnection,
  RouteChildNodeDataType,
  RouteNodeType,
} from '@lp/features/RoutePlanner/types';
import {
  AircraftLegResource,
  AviaLegTypes,
  BaseLegResourceType,
  GroundLegTypes,
  SegmentTypeEnum,
} from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { convertMsToTimeString } from '@/utils/helpers';
import { REPOSITION_SEGMENT_TYPES } from '@/utils/route';
import { RouteSegmentType } from '@/types/route';
import { getChildSourceHandleType, getChildTargetHandleType } from './handle';
import { ALT_EDGE_WIDTH_MIN, ALT_MAX_EDGE_WIDTH_SUM, EDGE_WIDTH } from './consts';
import { getSegmentLeg } from './route';

const getBaseData = (
  currentLeg: CombinedRouteLegType<AviaLegTypes | GroundLegTypes>,
  aircrafts: BaseLegResourceType<AircraftLegResource>[],
) => {
  const currentAircraftResource = currentLeg.resources?.find((l) => l.resourceType === CaseResourceType.Aircraft);
  const currentAircraft = currentAircraftResource && aircrafts.find((a) => a.id === currentAircraftResource.resourceId);
  const isMultipleAircrafts = aircrafts.length > 1;
  const currentAircraftIndex = isMultipleAircrafts ? aircrafts.findIndex((a) => a.id === currentAircraft?.id) : -1;
  const time = convertMsToTimeString(currentLeg.segmentDurationInMs || 0, 'short');

  return {
    legType: currentLeg.legType,
    time,
    index: currentAircraftIndex + 1,
    isReposition: REPOSITION_SEGMENT_TYPES.includes(currentLeg.segmentType),
    aircraft: currentAircraft?.data,
  };
};

export const getParentEdgeData = (
  node: RouteNodeType,
  prevNode: RouteNodeType,
  currentLeg: CombinedRouteLegType<AviaLegTypes | GroundLegTypes> | null,
  aircrafts: BaseLegResourceType<AircraftLegResource>[],
) => {
  const baseData = {
    id: `e${prevNode.id}-${node.id}`,
    source: prevNode.id,
    target: node.id,
    type: 'straightEdge',
    sourceHandle: NodeConnection.fromMainRight,
    targetHandle: NodeConnection.toMainLeft,
  };

  if (!currentLeg) {
    return baseData;
  }

  return {
    ...baseData,
    data: getBaseData(currentLeg, aircrafts),
  };
};

type GetChildEdgeDataParams = {
  node: RouteNodeType | RouteNodeType<RouteChildNodeDataType>;
  prevNode: RouteNodeType | RouteNodeType<RouteChildNodeDataType>;
  currentLeg: CombinedRouteLegType<AviaLegTypes | GroundLegTypes> | null;
  aircrafts: BaseLegResourceType<AircraftLegResource>[];
  connectionType: 'from' | 'to';
  index: number;
  edgeType: 'straightEdge' | 'stepEdge';
};

const getChildEdgeData = ({
  node,
  prevNode,
  currentLeg,
  aircrafts,
  connectionType,
  index,
  edgeType = 'stepEdge',
}: GetChildEdgeDataParams) => {
  const targetHandle = getChildTargetHandleType(node, connectionType, index);
  const sourceHandle = getChildSourceHandleType(prevNode, connectionType, index);

  const baseData = {
    id: `e${prevNode.id}-${node.id}`,
    source: prevNode.id,
    target: node.id,
    type: edgeType,
    sourceHandle,
    targetHandle,
  };

  if (!currentLeg) {
    return baseData;
  }

  return {
    ...baseData,
    data: getBaseData(currentLeg, aircrafts),
  };
};

type GetChildEdgeParams = {
  currentNode: RouteNodeType<RouteChildNodeDataType>;
  parentNode: RouteNodeType;
  currentLeg: CombinedRouteLegType<AviaLegTypes | GroundLegTypes> | null;
  aircrafts: BaseLegResourceType<AircraftLegResource>[];
  connectionType: 'from' | 'to';
  index: number;
};

export const getChildEdge = ({
  currentNode,
  parentNode,
  currentLeg,
  connectionType,
  aircrafts,
  index,
}: GetChildEdgeParams) => {
  const isFrom = connectionType === 'from';
  const prevNode = isFrom ? parentNode : currentNode;
  const node = isFrom ? currentNode : parentNode;
  const edgeType = index === 2 ? 'straightEdge' : 'stepEdge';

  return getChildEdgeData({ node, prevNode, currentLeg, aircrafts, connectionType, index, edgeType });
};

type GetMainEdgeDataParams = {
  node: RouteNodeType;
  nodes: RouteNodeType[];
  segmentsById: Map<string, RouteSegmentType>;
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  aircrafts: BaseLegResourceType<AircraftLegResource>[];
};

export const getMainEdgeData = ({ node, nodes, segmentsById, legs, aircrafts }: GetMainEdgeDataParams) => {
  const isReposition = REPOSITION_SEGMENT_TYPES.includes(node.data.segmentType as SegmentTypeEnum);
  const mainNodes = isReposition
    ? nodes.filter(
        (n) => ![...REPOSITION_SEGMENT_TYPES, SegmentTypeEnum.custom].includes(n.data.segmentType as SegmentTypeEnum),
      )
    : nodes;
  const prevNode = mainNodes[mainNodes.length - 1];

  const isPrevReposition = REPOSITION_SEGMENT_TYPES.includes(prevNode.data.segmentType as SegmentTypeEnum);
  const nodeForEdge = isPrevReposition ? prevNode : node;
  const currentLeg = getSegmentLeg(segmentsById, legs, nodeForEdge);

  return getParentEdgeData(node, prevNode, currentLeg, aircrafts);
};

export const getDefaultEdgeWidth = (parentNodes: RouteNodeType[], schemaType: 'main' | 'alt' = 'main') => {
  const altEdgeWidth =
    parentNodes.length > 6 ? ALT_EDGE_WIDTH_MIN : Math.round(ALT_MAX_EDGE_WIDTH_SUM / (parentNodes.length - 1));
  return schemaType === 'main' ? EDGE_WIDTH : altEdgeWidth;
};

export const hasChildren = (nodeItem?: RouteNodeType, maxCount: number = 0) => {
  if (!nodeItem) {
    return false;
  }

  return (nodeItem.data.childrenTop || 0) >= maxCount || (nodeItem.data.childrenBottom || 0) >= maxCount;
};

export const getCurrentEdgeWidth = (
  currentNodeItem?: RouteNodeType,
  prevNodeItem?: RouteNodeType,
  edgeWidth: number = 0,
) => {
  const prevNodeHasChildren = hasChildren(prevNodeItem, 2);
  const currentNodeHasChildren = hasChildren(currentNodeItem, 1);

  return prevNodeHasChildren && currentNodeHasChildren ? edgeWidth * 2 : edgeWidth;
};
